export type FilterParameterValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean | null>
  | null
  | undefined;

export type FilterParameters = Record<string, FilterParameterValue>;

export type SavedBrowserFilters = {
  query?: string;
  kind?: string | null;
  sort?: string | null;
  direction?: string | null;
  scope?: string | null;
  favoriteFilter?: string | null;
};

export type StoredFilterMemory = {
  parameters?: FilterParameters;
  savedBrowser?: SavedBrowserFilters;
  updatedAt?: string;
};

export function preferRecentFilterParameters(
  local: StoredFilterMemory | null,
  server: { parameters?: FilterParameters; updated_at?: string } | null,
): FilterParameters | undefined {
  if (!server?.parameters) return local?.parameters;
  if (!local?.parameters) return server.parameters;

  const localTime = Date.parse(local.updatedAt || "");
  const serverTime = Date.parse(server.updated_at || "");
  if (Number.isFinite(localTime) && (!Number.isFinite(serverTime) || localTime > serverTime)) {
    return local.parameters;
  }
  return server.parameters;
}

export type ParameterSlugMap = Record<string, string>;

const STORAGE_PREFIX = "rtFilterMemory:v1";

const IGNORED_URL_PARAMS = new Set([
  "action_buttons",
  "background",
  "bordered",
  "downloads",
  "header",
  "hide_parameters",
  "logo",
  "refresh",
  "side_nav",
  "theme",
  "titled",
  "top_nav",
]);

function storageKey(userKey: string | null | undefined, resourceKey: string) {
  return `${STORAGE_PREFIX}:${userKey || "anonymous"}:${resourceKey}`;
}

function isPlainObject(value: unknown): value is Record<string, any> {
  return Object.prototype.toString.call(value) === "[object Object]";
}

// Metabase's SDK state contains serialized query/cache operation keys. They
// can sit near real parameter state and must never be treated as selections.
function isSdkOperationString(value: unknown): value is string {
  if (typeof value !== "string") return false;
  return /^(?:get|search)DashboardParameterValues\s*\(/i.test(value.trim());
}

function isValidParameterScalar(value: unknown) {
  return (
    value == null ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    (typeof value === "string" && !isSdkOperationString(value))
  );
}

function normalizeParameters(parameters: unknown): FilterParameters | undefined {
  if (!isPlainObject(parameters)) return undefined;

  const normalized: FilterParameters = {};

  for (const [key, rawValue] of Object.entries(parameters)) {
    if (!key || rawValue === undefined) continue;

    if (rawValue === null) {
      normalized[key] = null;
      continue;
    }

    if (Array.isArray(rawValue)) {
      normalized[key] = rawValue
        .filter(isValidParameterScalar)
        .map((item) => item as string | number | boolean | null);
      if ((normalized[key] as unknown[]).length === 0 && rawValue.length > 0) {
        delete normalized[key];
      }
      continue;
    }

    if (isValidParameterScalar(rawValue) && rawValue !== null) {
      normalized[key] = rawValue as string | number | boolean;
    }
  }

  return Object.keys(normalized).length ? normalized : undefined;
}

export function readFilterMemory(
  userKey: string | null | undefined,
  resourceKey: string,
): StoredFilterMemory | null {
  try {
    const raw = localStorage.getItem(storageKey(userKey, resourceKey));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!isPlainObject(parsed)) return null;

    return parsed as StoredFilterMemory;
  } catch {
    return null;
  }
}

export function writeFilterMemory(
  userKey: string | null | undefined,
  resourceKey: string,
  patch: Omit<StoredFilterMemory, "updatedAt">,
) {
  try {
    const current = readFilterMemory(userKey, resourceKey) || {};
    const next: StoredFilterMemory = {
      ...current,
      ...patch,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(storageKey(userKey, resourceKey), JSON.stringify(next));
  } catch {
    // localStorage can be unavailable in private browsing or full-disk states.
  }
}

export function mergeRememberedParameters(
  remembered?: FilterParameters,
  defaults?: FilterParameters,
  options?: {
    hiddenParameters?: string[];
    allowedValues?: Record<string, FilterParameterValue[]>;
    requiredParameters?: string[];
  },
) {
  const effectiveDefaults: FilterParameters = { ...(defaults || {}) };
  const allowedAirports = normalizeAllowedAirportValues(options?.allowedValues?.airport);
  const airportRequired = (options?.requiredParameters || []).some(
    (name) => String(name).toLowerCase() === "airport",
  );

  // `defaults` here can itself be the output of an earlier
  // mergeRememberedParameters() call (ReportTool chains two calls together).
  // In that case an explicit `airport: null` already means "the user cleared
  // this on a report that doesn't require it" -- distinct from the key being
  // absent, which just means no one has decided a value yet. Only the latter
  // should fall back to the allowlist when airport isn't required.
  const isExplicitOptionalAirportClear =
    Object.prototype.hasOwnProperty.call(effectiveDefaults, "airport") &&
    effectiveDefaults.airport === null &&
    !airportRequired;

  // The API's airport allowlist is also a valid source for the initial filter
  // selection. This keeps the portal usable when a role/user lock grants an
  // airport that is not present in tenant_airports.
  if (
    allowedAirports.length > 0 &&
    isEmptyParameterValue(effectiveDefaults.airport) &&
    !isExplicitOptionalAirportClear
  ) {
    effectiveDefaults.airport =
      allowedAirports.length === 1 ? allowedAirports[0] : allowedAirports;
  }

  const sanitizedRemembered = sanitizeRememberedParameters(
    remembered,
    options?.requiredParameters,
  );
  const merged: FilterParameters = {
    ...effectiveDefaults,
    ...(sanitizedRemembered || {}),
  };

  for (const key of options?.hiddenParameters || []) {
    if (Object.prototype.hasOwnProperty.call(effectiveDefaults, key)) {
      merged[key] = effectiveDefaults[key];
    }
  }

  return merged;
}

function normalizeAllowedAirportValues(values?: FilterParameterValue[]) {
  if (!values) return [];
  return values
    .map((value) => normalizeAirportValue(value))
    .filter((value): value is string => Boolean(value));
}

function sanitizeRememberedParameters(
  remembered?: FilterParameters,
  requiredParameters?: string[],
) {
  if (!remembered) return undefined;

  const sanitized = normalizeParameters(remembered);
  if (!sanitized) return undefined;
  const airportRequired = (requiredParameters || []).some(
    (name) => String(name).toLowerCase() === "airport",
  );

  // A curated "only choose from this list" restriction on airport isn't
  // implemented yet -- today the lock's allowed_values exists solely to
  // supply the initial default (see the backfill below in
  // mergeRememberedParameters), not to validate a value the user picked.
  // The only thing allowed to reject an airport value here is the
  // report/lock's required flag rejecting an explicit clear.
  if (
    Object.prototype.hasOwnProperty.call(sanitized, "airport") &&
    isEmptyParameterValue(sanitized.airport)
  ) {
    if (airportRequired) {
      delete sanitized.airport;
    } else {
      sanitized.airport = null;
    }
  }

  return Object.keys(sanitized).length ? sanitized : undefined;
}

function normalizeAirportValue(value: FilterParameterValue) {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  const normalized = String(value).trim().toUpperCase();
  return normalized || undefined;
}

export function mapParameterKeys(
  parameters?: FilterParameters,
  parameterSlugMap?: ParameterSlugMap,
): FilterParameters | undefined {
  if (!parameters) return undefined;

  const mapped: FilterParameters = {};

  for (const [key, value] of Object.entries(parameters)) {
    mapped[parameterSlugMap?.[key] || key] = value;
  }

  return Object.keys(mapped).length ? mapped : undefined;
}

export function mergeNonEmptyParameters(
  current?: FilterParameters,
  next?: FilterParameters,
) {
  const merged: FilterParameters = { ...(current || {}) };

  for (const [key, value] of Object.entries(next || {})) {
    if (!key || isEmptyParameterValue(value)) continue;
    merged[key] = value;
  }

  return Object.keys(merged).length ? merged : undefined;
}

export function applyParameterPatch(
  current?: FilterParameters,
  next?: FilterParameters,
  options?: { clearEmpty?: boolean },
) {
  const merged: FilterParameters = { ...(current || {}) };

  for (const [key, value] of Object.entries(next || {})) {
    if (!key) continue;

    if (isEmptyParameterValue(value)) {
      if (options?.clearEmpty) merged[key] = null;
      continue;
    }

    merged[key] = value;
  }

  return Object.keys(merged).length ? merged : undefined;
}

export function extractParameterSlugMap(data: unknown): ParameterSlugMap {
  const seen = new Set<unknown>();
  const queue: Array<{ value: unknown; depth: number }> = [{ value: data, depth: 0 }];
  const map: ParameterSlugMap = {};

  while (queue.length) {
    const item = queue.shift();
    if (!item) continue;

    const { value, depth } = item;
    if (!value || seen.has(value)) continue;
    seen.add(value);

    if (Array.isArray(value)) {
      for (const entry of value) {
        addParameterMapEntry(map, entry);
      }

      if (depth < 8) {
        value.forEach((child) => queue.push({ value: child, depth: depth + 1 }));
      }
      continue;
    }

    if (!isPlainObject(value)) continue;

    addParameterMapEntry(map, value);

    for (const key of ["parameters", "parameter_mappings", "parameterMappings", "dashcards", "ordered_cards"]) {
      if (value[key]) queue.push({ value: value[key], depth: depth + 1 });
    }

    if (depth < 3) {
      Object.values(value).forEach((child) =>
        queue.push({ value: child, depth: depth + 1 }),
      );
    }
  }

  return map;
}

export function extractParameterKeys(data: unknown): Set<string> {
  const seen = new Set<unknown>();
  const queue: Array<{ value: unknown; depth: number; fromParameters: boolean }> = [
    { value: data, depth: 0, fromParameters: false },
  ];
  const keys = new Set<string>();

  while (queue.length) {
    const item = queue.shift();
    if (!item) continue;

    const { value, depth, fromParameters } = item;
    if (!value || seen.has(value)) continue;
    seen.add(value);

    if (Array.isArray(value)) {
      if (fromParameters) {
        value.forEach((entry) => addParameterKey(keys, entry));
      }
      if (depth < 6) {
        value.forEach((child) =>
          queue.push({ value: child, depth: depth + 1, fromParameters }),
        );
      }
      continue;
    }

    if (!isPlainObject(value)) continue;

    if (fromParameters) addParameterKey(keys, value);

    for (const key of ["parameters", "parameter_mappings", "parameterMappings"]) {
      if (value[key]) {
        queue.push({ value: value[key], depth: depth + 1, fromParameters: true });
      }
    }

    for (const key of ["dashcards", "ordered_cards"]) {
      if (value[key]) {
        queue.push({ value: value[key], depth: depth + 1, fromParameters: false });
      }
    }

    if (depth < 2) {
      Object.values(value).forEach((child) =>
        queue.push({ value: child, depth: depth + 1, fromParameters: false }),
      );
    }
  }

  return keys;
}

export function filterToKnownParameters(
  parameters: FilterParameters | undefined,
  knownKeys?: Set<string>,
  options?: { dropEmpty?: boolean },
): FilterParameters | undefined {
  if (!parameters) return undefined;

  const filtered: FilterParameters = {};
  const hasKnownKeys = Boolean(knownKeys?.size);

  for (const [key, value] of Object.entries(parameters)) {
    if (!key || value === undefined) continue;
    if (IGNORED_URL_PARAMS.has(key)) continue;
    if (hasKnownKeys && !knownKeys?.has(key)) continue;
    if (options?.dropEmpty && isEmptyParameterValue(value)) continue;
    filtered[key] = value;
  }

  return Object.keys(filtered).length ? filtered : undefined;
}

function isEmptyParameterValue(value: FilterParameterValue) {
  if (value == null) return true;
  if (Array.isArray(value)) {
    return value.length === 0 || value.every((item) => item == null || item === "");
  }
  return value === "";
}

function addUrlSearchParams(params: FilterParameters, searchParams: URLSearchParams) {
  for (const key of Array.from(searchParams.keys())) {
    if (IGNORED_URL_PARAMS.has(key)) continue;

    const values = searchParams.getAll(key).filter((value) => value !== "");
    if (!values.length) continue;

    params[key] = values.length === 1 ? values[0] : values;
  }
}

export function extractParametersFromUrl(urlString: string): FilterParameters | undefined {
  try {
    const [baseAndQuery, hash] = urlString.split("#");
    const url = new URL(baseAndQuery, window.location.origin);
    const params: FilterParameters = {};

    addUrlSearchParams(params, url.searchParams);

    if (hash) {
      addUrlSearchParams(params, new URLSearchParams(hash));
    }

    return Object.keys(params).length ? params : undefined;
  } catch {
    return undefined;
  }
}

export function applyRememberedParametersToUrl(
  urlString: string,
  remembered?: FilterParameters,
  defaults?: FilterParameters,
  options?: Parameters<typeof mergeRememberedParameters>[2],
) {
  const merged = mergeRememberedParameters(remembered, defaults, options);
  if (!Object.keys(merged).length) return urlString;

  try {
    const [baseAndQuery, hash] = urlString.split("#");
    const url = new URL(baseAndQuery, window.location.origin);

    for (const [key, rawValue] of Object.entries(merged)) {
      if (!key || IGNORED_URL_PARAMS.has(key) || rawValue == null) continue;

      url.searchParams.delete(key);
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      values.forEach((value) => {
        if (value != null) url.searchParams.append(key, String(value));
      });
    }

    return hash ? `${url.toString()}#${hash}` : url.toString();
  } catch {
    return urlString;
  }
}

export function extractParametersFromMessage(data: unknown): FilterParameters | undefined {
  return extractParametersFromObject(data);
}

export function extractParametersFromObject(data: unknown): FilterParameters | undefined {
  const seen = new Set<unknown>();
  const queue: Array<{ value: unknown; depth: number; path: string }> = [
    { value: data, depth: 0, path: "" },
  ];
  const candidates: FilterParameters[] = [];

  while (queue.length) {
    const item = queue.shift();
    if (!item) continue;

    const { value: current, depth, path } = item;
    if (!current || seen.has(current)) continue;
    seen.add(current);

    if (Array.isArray(current)) {
      const fromParameterList = parametersFromList(current);
      if (fromParameterList) candidates.push(fromParameterList);
      if (depth < 8) {
        current.forEach((value, index) =>
          queue.push({ value, depth: depth + 1, path: `${path}.${index}` }),
        );
      }
      continue;
    }

    if (!isPlainObject(current)) continue;

    const fromValueObject = parameterFromValueObject(current);
    if (fromValueObject) candidates.push(fromValueObject);

    for (const key of [
      "parameterValues",
      "parameter_values",
      "parameter-values",
      "parameterValuesBySlug",
      "parameterValuesById",
      "parameters",
      "initialParameters",
      "initialSqlParameters",
    ]) {
      const extracted = normalizeParameters(current[key]);
      if (extracted) candidates.push(extracted);

      if (Array.isArray(current[key])) {
        const fromParameterList = parametersFromList(current[key]);
        if (fromParameterList) candidates.push(fromParameterList);
      }
    }

    if (depth < 8 && queue.length < 5000) {
      for (const [key, value] of Object.entries(current)) {
        if (key === "data" || key === "result" || key === "rows") continue;
        queue.push({ value, depth: depth + 1, path: `${path}.${key}` });
      }
    }
  }

  return chooseBestParameterCandidate(candidates);
}

export function extractParametersFromRequestBody(body: unknown): FilterParameters | undefined {
  if (!body) return undefined;

  if (typeof body === "string") {
    const trimmed = body.trim();
    if (!trimmed) return undefined;

    try {
      return extractParametersFromObject(JSON.parse(trimmed));
    } catch {
      return extractParametersFromUrl(`/?${trimmed}`);
    }
  }

  if (body instanceof URLSearchParams) {
    return extractParametersFromUrl(`/?${body.toString()}`);
  }

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    const params: FilterParameters = {};
    body.forEach((value, key) => {
      if (typeof value === "string") params[key] = value;
    });
    return Object.keys(params).length ? params : undefined;
  }

  return extractParametersFromObject(body);
}

export function extractParameterArrayFromRequestBody(
  body: unknown,
): FilterParameters | undefined {
  const parsed = parseRequestBody(body);
  if (!isPlainObject(parsed) || !Array.isArray(parsed.parameters)) {
    return undefined;
  }

  return parametersFromList(parsed.parameters);
}

function parseRequestBody(body: unknown): unknown {
  if (!body) return undefined;

  if (typeof body === "string") {
    const trimmed = body.trim();
    if (!trimmed) return undefined;

    try {
      return JSON.parse(trimmed);
    } catch {
      return undefined;
    }
  }

  return body;
}

function parametersFromList(items: unknown[]): FilterParameters | undefined {
  const params: FilterParameters = {};

  for (const item of items) {
    if (!isPlainObject(item)) continue;

    const key =
      typeof item.slug === "string"
        ? item.slug
        : typeof item.name === "string"
          ? item.name
          : typeof item.parameter_slug === "string"
            ? item.parameter_slug
            : typeof item.parameterSlug === "string"
              ? item.parameterSlug
              : typeof item.parameter_id === "string" || typeof item.parameter_id === "number"
                ? item.parameter_id
                : typeof item.parameterId === "string" || typeof item.parameterId === "number"
                  ? item.parameterId
                  : typeof item.id === "string" || typeof item.id === "number"
                    ? item.id
                    : null;

    if (!key || !Object.prototype.hasOwnProperty.call(item, "value")) continue;

    const normalized = normalizeParameters({ [String(key)]: item.value });
    if (normalized) Object.assign(params, normalized);
  }

  return Object.keys(params).length ? params : undefined;
}

function parameterFromValueObject(item: Record<string, any>): FilterParameters | undefined {
  if (!Object.prototype.hasOwnProperty.call(item, "value")) return undefined;

  const rawKey =
    typeof item.slug === "string"
      ? item.slug
      : typeof item.name === "string"
        ? item.name
        : typeof item.parameterId === "string" || typeof item.parameterId === "number"
          ? item.parameterId
          : typeof item.parameter_id === "string" || typeof item.parameter_id === "number"
            ? item.parameter_id
            : typeof item.id === "string" || typeof item.id === "number"
              ? item.id
              : null;

  if (rawKey == null) return undefined;

  return normalizeParameters({ [String(rawKey)]: item.value });
}

function addParameterMapEntry(map: ParameterSlugMap, item: unknown) {
  if (!isPlainObject(item)) return;

  const id =
    typeof item.id === "string" || typeof item.id === "number"
      ? String(item.id)
      : typeof item.parameter_id === "string" || typeof item.parameter_id === "number"
        ? String(item.parameter_id)
        : typeof item.parameterId === "string" || typeof item.parameterId === "number"
          ? String(item.parameterId)
          : null;

  const slug =
    typeof item.slug === "string"
      ? item.slug
      : typeof item.name === "string"
        ? item.name
        : typeof item.parameter_slug === "string"
          ? item.parameter_slug
          : typeof item.parameterSlug === "string"
            ? item.parameterSlug
            : null;

  if (id && slug) map[id] = slug;
}

function addParameterKey(keys: Set<string>, item: unknown) {
  if (!isPlainObject(item)) return;

  for (const value of [
    item.slug,
    item.name,
    item.parameter_slug,
    item.parameterSlug,
    item.id,
    item.parameter_id,
    item.parameterId,
  ]) {
    if (typeof value === "string" && value.trim()) keys.add(value);
    if (typeof value === "number") keys.add(String(value));
  }
}

function chooseBestParameterCandidate(candidates: FilterParameters[]) {
  let best: FilterParameters | undefined;
  let bestScore = 0;

  for (const candidate of candidates) {
    const entries = Object.entries(candidate).filter(
      ([key, value]) =>
        key &&
        value !== undefined &&
        !["id", "name", "slug", "type"].includes(key),
    );

    if (!entries.length) continue;

    const score = entries.reduce((total, [, value]) => {
      if (Array.isArray(value)) return total + Math.max(1, value.length);
      if (value == null || value === "") return total;
      return total + 1;
    }, 0);

    if (score > bestScore) {
      bestScore = score;
      best = Object.fromEntries(entries) as FilterParameters;
    }
  }

  return best;
}
